/**
 * Company Management Page
 * Requirements: 14.1-14.4 - Company management
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { organizationApi, type CompanyExportRow } from '../api/organizationApi';
import type { Company } from '../types/organization.types';

/** 会社明細（請求先・最新注文込み）を CSV 文字列にする（Excel 向けに BOM 付き）。 */
const buildCompaniesCsv = (rows: CompanyExportRow[]): string => {
  const header = [
    '会社ID',
    '会社名',
    'GB人財',
    'GB組織',
    'GBなび',
    'レポート開示',
    '社員レポートボタン',
    'QRコード発行日',
    '会社作成日',
    '顧客番号',
    '請求先社名',
    '郵便番号',
    '住所',
    '電話',
    '代表者名',
    'メール',
    '決済担当者メール',
    '支払方法',
    '支払ステータス',
    '請求先作成日',
    '最新注文ID',
    '注文ステータス',
    '税込合計',
    '申込日',
    '明細数',
  ];
  const yn = (b?: boolean) => (b ? '有' : '無');
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) =>
    [
      r.id,
      r.name,
      yn(r.hasJinzai),
      yn(r.hasSoshiki),
      yn(r.hasNavi),
      yn(r.reportVisible),
      yn(r.employeeReportButtonVisible),
      r.qrIssuedAt ?? '',
      r.createdAt ?? '',
      r.billing?.customerNumber ?? '',
      r.billing?.name ?? '',
      r.billing?.zip ?? '',
      r.billing?.address ?? '',
      r.billing?.tel ?? '',
      r.billing?.presidentName ?? '',
      r.billing?.email ?? '',
      r.billing?.settlementEmail ?? '',
      r.billing?.paymentMethodChoice ?? '',
      r.billing?.paymentMethodStatus ?? '',
      r.billing?.createdAt ?? '',
      r.latestOrder?.id ?? '',
      r.latestOrder?.status ?? '',
      r.latestOrder?.totalAmountIncluded ?? '',
      r.latestOrder?.createdAt ?? '',
      r.latestOrder?.items?.length ?? '',
    ]
      .map(esc)
      .join(',')
  );
  return '﻿' + [header.join(','), ...lines].join('\r\n');
};

/** CSV 文字列をファイルとしてダウンロードさせる。 */
const downloadCsv = (csv: string, fileName: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const CompanyManagementPage = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyName, setCompanyName] = useState('');
  // 会社単位の設定（編集時のみ）。未設定は既定 true（現状維持）。
  const [settings, setSettings] = useState({
    hasJinzai: true,
    hasSoshiki: true,
    hasNavi: true,
    reportVisible: true,
    employeeReportButtonVisible: true,
    qrIssuedAt: '' as string,
  });

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await organizationApi.getCompanies();
      setCompanies(data);
    } catch (err) {
      setError('会社一覧の取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 全会社情報（請求先・最新注文込み）を CSV でダウンロードする。
  const handleExportCsv = async () => {
    try {
      setExporting(true);
      setError(null);
      const rows = await organizationApi.getCompaniesExport();
      const today = new Date().toISOString().slice(0, 10);
      downloadCsv(buildCompaniesCsv(rows), `companies-${today}.csv`);
      setSuccess(`全 ${rows.length} 社の情報をダウンロードしました`);
    } catch {
      setError('会社情報のダウンロードに失敗しました');
    } finally {
      setExporting(false);
    }
  };

  const handleOpenDialog = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setCompanyName(company.name);
      setSettings({
        hasJinzai: company.hasJinzai ?? true,
        hasSoshiki: company.hasSoshiki ?? true,
        hasNavi: company.hasNavi ?? true,
        reportVisible: company.reportVisible ?? true,
        employeeReportButtonVisible: company.employeeReportButtonVisible ?? true,
        qrIssuedAt: company.qrIssuedAt ?? '',
      });
    } else {
      setEditingCompany(null);
      setCompanyName('');
      setSettings({
        hasJinzai: true,
        hasSoshiki: true,
        hasNavi: true,
        reportVisible: true,
        employeeReportButtonVisible: true,
        qrIssuedAt: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCompany(null);
    setCompanyName('');
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (editingCompany) {
        await organizationApi.updateCompany(editingCompany.id, {
          name: companyName,
          hasJinzai: settings.hasJinzai,
          hasSoshiki: settings.hasSoshiki,
          hasNavi: settings.hasNavi,
          reportVisible: settings.reportVisible,
          employeeReportButtonVisible: settings.employeeReportButtonVisible,
          qrIssuedAt: settings.qrIssuedAt || null,
        });
        setSuccess('会社情報を更新しました');
      } else {
        await organizationApi.createCompany({ name: companyName });
        setSuccess('会社を登録しました');
      }
      handleCloseDialog();
      await loadCompanies();
    } catch (err) {
      setError(editingCompany ? '会社情報の更新に失敗しました' : '会社の登録に失敗しました');
      console.error(err);
    }
  };

  const handleOpenDeleteDialog = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
    setDeleteConfirmText('');
  };

  const handleDelete = async () => {
    if (!companyToDelete || deleteConfirmText !== companyToDelete.name) {
      return;
    }

    try {
      setError(null);
      await organizationApi.deleteCompany(companyToDelete.id, true);
      setSuccess('会社を削除しました');
      handleCloseDeleteDialog();
      await loadCompanies();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as {
          response?: {
            data?: {
              requiresConfirmation?: boolean;
              employeeCount?: number;
              departmentCount?: number;
            };
          };
        };
        if (axiosError.response?.data?.requiresConfirmation) {
          setError(
            `この会社には${axiosError.response.data.departmentCount || 0}個の部門と${axiosError.response.data.employeeCount || 0}名の従業員が所属しています。削除を確認してください。`
          );
        } else {
          setError('会社の削除に失敗しました');
        }
      } else {
        setError('会社の削除に失敗しました');
      }
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">会社管理</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={handleExportCsv}
            disabled={exporting}
          >
            {exporting ? '出力中…' : '全会社情報をCSVダウンロード'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            会社を追加
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>会社名</TableCell>
                  <TableCell>登録日</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      会社が登録されていません
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell>{company.name}</TableCell>
                      <TableCell>{new Date(company.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleOpenDialog(company)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleOpenDeleteDialog(company)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCompany ? '会社情報編集' : '会社追加'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="会社名"
            fullWidth
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />

          {editingCompany && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                利用機能（購入エンタイトルメント）
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.hasJinzai}
                    onChange={(e) => setSettings((s) => ({ ...s, hasJinzai: e.target.checked }))}
                  />
                }
                label="GB人財を利用可能にする"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.hasSoshiki}
                    onChange={(e) => setSettings((s) => ({ ...s, hasSoshiki: e.target.checked }))}
                  />
                }
                label="GB組織を利用可能にする"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.hasNavi}
                    onChange={(e) => setSettings((s) => ({ ...s, hasNavi: e.target.checked }))}
                  />
                }
                label="GBなびを利用可能にする"
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                レポート開示
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.reportVisible}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, reportVisible: e.target.checked }))
                    }
                  />
                }
                label="レポートを表示する"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.employeeReportButtonVisible}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, employeeReportButtonVisible: e.target.checked }))
                    }
                  />
                }
                label="社員向け「個人レポート」ボタンを表示する"
              />
              <TextField
                label="QRコード発行日"
                type="date"
                value={settings.qrIssuedAt}
                onChange={(e) => setSettings((s) => ({ ...s, qrIssuedAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
                helperText="社員ログインQRを発行した日（任意）"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained" disabled={!companyName.trim()}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>会社の削除</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            この操作は取り消せません。会社を削除すると、関連する部門と従業員の割り当ても削除されます。
          </Typography>
          <Typography gutterBottom sx={{ mt: 2 }}>
            削除を確認するには、会社名「<strong>{companyToDelete?.name}</strong>
            」を入力してください:
          </Typography>
          <TextField
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={companyToDelete?.name}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteConfirmText !== companyToDelete?.name}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
